import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";

// Encapsulated global state for popover
const PopoverState = {
  popover: null,
  popperInstance: null,
  closeHandler: null,
  hideTimeout: null
};

export default class extends Controller {
  static values = {
    title: String,
    content: String,
    placement: { type: String, default: "top" }
  };

  // Handler bindings as class properties
  _showPopover = this.showPopover.bind(this);
  _schedulePopoverHide = this.schedulePopoverHide.bind(this);
  _clearScheduledPopoverHide = this.clearScheduledPopoverHide.bind(this);
  _closePopover = this.hidePopover.bind(this);

  connect() {
    this.element.addEventListener("mouseenter", this._showPopover);
    this.element.addEventListener("mouseleave", this._schedulePopoverHide);
    this.element.addEventListener("focus", this._showPopover);
    this.element.addEventListener("blur", this._schedulePopoverHide);
  }

  showPopover() {
    this.clearScheduledPopoverHide();
    if (!PopoverState.popover) {
      PopoverState.popover = document.createElement("div");
      PopoverState.popover.className = "run-log-popover-instance absolute z-[11000] min-w-[220px] max-w-xs bg-white rounded-xl shadow-2xl p-4 border border-gray-200 text-[#2b353f] font-sans text-[15px] animate-fadeIn";
      document.body.appendChild(PopoverState.popover);
    }
    if (PopoverState.closeHandler) {
      const closeBtn = PopoverState.popover.querySelector('[data-run-log-popover-target="closeButton"]');
      closeBtn?.removeEventListener('click', PopoverState.closeHandler);
    }
    PopoverState.popover.innerHTML = this._popoverMarkup();
    if (PopoverState.popperInstance) {
      PopoverState.popperInstance.destroy();
    }
    PopoverState.popperInstance = createPopper(this.element, PopoverState.popover, {
      placement: this.placementValue,
      modifiers: [
        { name: 'offset', options: { offset: [0, 12] } }
      ]
    });
    PopoverState.closeHandler = this._closePopover;
    const closeBtn = PopoverState.popover.querySelector('[data-run-log-popover-target="closeButton"]');
    closeBtn.addEventListener('click', PopoverState.closeHandler);

    // Add mouseenter/mouseleave to popover to control hiding
    PopoverState.popover.addEventListener('mouseenter', this._clearScheduledPopoverHide);
    PopoverState.popover.addEventListener('mouseleave', this._schedulePopoverHide);
  }

  schedulePopoverHide() {
    this.clearScheduledPopoverHide();
    PopoverState.hideTimeout = setTimeout(() => this.hidePopover(), 250);
  }

  clearScheduledPopoverHide() {
    if (PopoverState.hideTimeout) {
      clearTimeout(PopoverState.hideTimeout);
      PopoverState.hideTimeout = null;
    }
  }

  hidePopover() {
    this.clearScheduledPopoverHide();
    if (!PopoverState.popover) return;
    const closeBtn = PopoverState.popover.querySelector('[data-run-log-popover-target="closeButton"]');
    closeBtn?.removeEventListener('click', PopoverState.closeHandler);
    PopoverState.popover.removeEventListener('mouseenter', this._clearScheduledPopoverHide);
    PopoverState.popover.removeEventListener('mouseleave', this._schedulePopoverHide);
    if (PopoverState.popperInstance) {
      PopoverState.popperInstance.destroy();
      PopoverState.popperInstance = null;
    }
    PopoverState.popover.remove();
    PopoverState.popover = null;
    PopoverState.closeHandler = null;
  }

  disconnect() {
    this.element.removeEventListener("mouseenter", this._showPopover);
    this.element.removeEventListener("mouseleave", this._schedulePopoverHide);
    this.element.removeEventListener("focus", this._showPopover);
    this.element.removeEventListener("blur", this._schedulePopoverHide);
    this.hidePopover();
  }

  _popoverMarkup() {
    return `
      <button type="button" data-run-log-popover-target="closeButton" class="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none p-2" aria-label="Close popover">&times;</button>
      <div data-run-log-popover-target="title" class="text-[16px] font-semibold mb-2 text-[#1a2233]">${this.titleValue || "Run Log Info"}</div>
      <div data-run-log-popover-target="content" class="text-gray-500 text-[15px]">${this.contentValue || "No details available."}</div>
    `;
  }
} 